import {
    AfterViewInit,
    Component,
    ElementRef,
    Input,
    OnChanges,
    SimpleChanges,
} from '@angular/core';
import { SVG } from '@svgdotjs/svg.js';

@Component({
    selector: 'app-sap-chart',
    templateUrl: './svg-system.component.html',
    styleUrls: ['./svg-system.component.scss'],
    standalone: false,
})
export class SapChartComponent implements AfterViewInit, OnChanges {
    @Input() data: any;
    @Input() cameraScale: string;

    constructor(private elementRef: ElementRef) {}

    ngAfterViewInit(): void {
        this.createChart(this.data);
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['data'] && !changes['data'].firstChange) {
            this.createChart(this.data);
        }
    }

    private createChart(data: any): void {
        const chartContainer = this.elementRef.nativeElement.querySelector('#sap-chart');
        while (chartContainer.firstChild) {
            chartContainer.removeChild(chartContainer.firstChild);
        }

        const draw = SVG()
            .addTo(chartContainer)
            .clear()
            .size('100%', '100%')
            .attr('preserveAspectRatio', 'xMinYMin meet');

        // Calculate the max depth of the tree
        const calculateMaxDepth = (node: any, depth = 0): number => {
            if (!node.children || node.children.length === 0) {
                return depth;
            }
            return Math.max(...node.children.map(child => calculateMaxDepth(child, depth + 1)));
        };

        const maxDepth = calculateMaxDepth(data);
        const nodeWidth = 100 / (maxDepth > 1 ? maxDepth * 1.5 : maxDepth + 1.25); // Dynamically set width based on tree depth

        const gapY = 2; // Gap between sibling nodes in %
        const baseGapX = 100; // Base gap before adjustments
        const gapX = baseGapX / (maxDepth > 1 ? maxDepth * 1.75 : maxDepth * 2.5);

        const childrenCount = node => (node.children ? node.children.length : 0);

        draw.viewbox(0, 0, 400, 800); // Dynamically adjust height

        // Updated width calculation
        const calculateNodeWidth = (node: any): number => {
            if (!node.children || node.children.length === 0) {
                return 33 / (childrenCount(data) || 2); // Leaf nodes take up calculated width
            }
            return node.children.reduce(
                (acc, child) => acc + calculateNodeWidth(child) + gapY,
                -gapY
            );
        };

        const drawNode = (node: any, y: number, x: number, height: number): void => {
            const width = nodeWidth; // Use the calculated dynamic width

            const rect = draw
                .rect(`${width}%`, `${height}%`)
                .move(`${x}%`, `${y}%`)
                .radius(5)
                .fill('#d3d3f9')
                .stroke({ color: '#666', width: 0.5 });

            // Get the actual bounding box dimensions (SVG.js returns pixels)
            const bbox = rect.bbox();

            // Calculate text position in absolute units
            const textX = bbox.x + bbox.width / 2;
            const textY = bbox.y + bbox.height / 2 - bbox.width * 0.1;

            const text = draw
                .text(node.title)
                .font({ size: bbox.width * 0.1 }) // Scale text dynamically
                .move(textX, textY);

            // Ensure text is centered
            text.attr({ 'text-anchor': 'middle', 'dominant-baseline': 'middle' });
        };

        const drawTree = (node: any, x: number, y: number, height: number): void => {
            drawNode(node, x, y, height);

            if (node.children && node.children.length > 0) {
                const totalWidth = node.children.reduce(
                    (acc, child) => acc + calculateNodeWidth(child) + gapY,
                    -gapY
                );
                let currentX = x + (height - totalWidth) / 2;

                node.children.forEach((child: any) => {
                    const childWidth = calculateNodeWidth(child);
                    const childX = currentX;
                    const childY = y + 10 + gapX; // Move to next level

                    drawTree(child, childX, childY, childWidth);
                    currentX += childWidth + gapY;
                });
            }
        };

        const rootHeight = calculateNodeWidth(data);
        const rootX = 1;
        const rootY = 1;

        drawTree(data, rootX, rootY, rootHeight);
    }
}
